import { useMutation } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import type { AppRouter } from '@viapeople/api'
import superjson from 'superjson'
import { config } from '../../../config'
import { useToast } from '../../../utils/ToastContext'
import type { CustomFieldFilter, ExcelExportOutput } from './reportTypes'

interface ExcelExportOptions {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

const apiURL = config.apiURL.endsWith('/') ? config.apiURL : `${config.apiURL}/`

const trpcClient = createTRPCClient<AppRouter>({
	links: [
		httpBatchLink({
			url: apiURL,
			fetch(url, options) {
				return fetch(url, {
					...options,
					credentials: 'include',
				})
			},
			transformer: superjson,
			headers: {
				'Content-Type': 'application/json',
			},
		}),
	],
})

export const useExcelExport = (options: ExcelExportOptions = {}) => {
	const { showToast } = useToast()

	const mutation = useMutation({
		mutationFn: (params: {
			reportType: string
			cycleIds: string[]
			filters?: CustomFieldFilter[]
			includeCustomFields?: boolean
		}) => trpcClient.client.reports.exportReportAsExcel.mutate(params),
		onSuccess: (result: ExcelExportOutput) => {
			// Handle both comma-separated bytes and base64 formats
			const bytes = result.data.includes(',')
				? new Uint8Array(result.data.split(',').map(Number))
				: (() => {
						const binaryString = atob(result.data)
						const bytes = new Uint8Array(binaryString.length)
						for (let i = 0; i < binaryString.length; i++) {
							bytes[i] = binaryString.charCodeAt(i)
						}
						return bytes
					})()

			const blob = new Blob([bytes], {
				type: result.contentType,
			})
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = result.filename
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)

			showToast('Excel file downloaded successfully')
			options.onSuccess?.()
		},
		onError: (error: Error) => {
			showToast(`Excel export failed: ${error.message}`, 'error')
			options.onError?.(error)
		},
	})

	const exportReport = (params: {
		reportType: string
		cycleIds: string[]
		filters?: CustomFieldFilter[]
		includeCustomFields?: boolean
	}) => {
		mutation.mutate(params)
	}

	return {
		exportReport,
		isLoading: mutation.isPending,
		error: mutation.error,
	}
}
